/**
 * StudentRosterReport.js
 * Full student roster — filterable list with stats summary.
 * Fetches from GET /students/search (name) or GET /students/?active=1 (all).
 * Falls back to /students/search?name= for listing all if no list endpoint exists.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  FaUsers, FaSearch, FaTimes, FaVenusMars, FaCalendarAlt,
  FaPhoneAlt, FaEnvelope, FaCheckCircle, FaBan,
  FaFilter, FaSortAlphaDown, FaMedal,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import { useTheme, getT, glassCard, getBrandColors} from "./ThemeContext";
import API_BASE from "./apiConfig";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useDateTimeFormat } from "./utils/formatters";
const BASE = API_BASE;

const T = {
  get blue() { return getBrandColors().blue; },
  get dark() { return getBrandColors().blueDark; },
  get yellow() { return getBrandColors().yellow; },
  gray:   "rgba(255,255,255,0.45)",
  glass:  "rgba(255,255,255,0.06)",
  blur:   "blur(20px)",
  bdr:    "1px solid rgba(255,255,255,0.10)",
  sh:     "0 4px 28px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.06)",
  pageBg: "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)",
  font:   "'Segoe UI', system-ui, sans-serif",
};


function age(dob) {
  if (!dob) return "—";
  return Math.floor((new Date() - new Date(dob)) / (365.25 * 864e5));
}

// ─── Stat pill ────────────────────────────────────────────────────────────────
function StatPill({ icon: Icon, label, value, color, bg, bdr }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 18px", borderRadius:14, background:bg, border:`1.5px solid ${bdr}`, minWidth:110 }}>
      <div style={{ width:34, height:34, borderRadius:10, background:`${color}18`, border:`1.5px solid ${color}28`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Icon size={14} style={{ color }} />
      </div>
      <div>
        <div style={{ fontSize:20, fontWeight:900, color, lineHeight:1, fontFamily:T.font }}>{value}</div>
        <div style={{ fontSize:10, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:T.font }}>{label}</div>
      </div>
    </div>
  );
}

// ─── Student card ─────────────────────────────────────────────────────────────
function StudentCard({ s, primary, secondary }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();
  const gc = glassCard(T);
  const initials = `${s.first_name?.[0]||""}${s.last_name?.[0]||""}`;
  const active   = s.active_status === 1 || s.active_status === true;
  return (
    <div style={{ ...gc, padding:"14px 18px", display:"flex", alignItems:"center", gap:14, transition:"transform 0.15s, box-shadow 0.15s" }}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 10px 28px rgba(0,0,0,0.50)";}}
      onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=T.glassSh;}}>
      {/* Avatar */}
      <div style={{ width:46, height:46, borderRadius:"50%", flexShrink:0, background:`linear-gradient(135deg,${secondary},#fff3a0)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:900, color:primary, overflow:"hidden", boxShadow:`0 2px 8px ${secondary}44` }}>
        {s.photo_url
          ? <img src={`${BASE}/${s.photo_url}`} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
          : initials}
      </div>

      {/* Name + status */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:800, fontSize:14, color:T.textPrimary, fontFamily:T.font }}>{s.first_name} {s.last_name}</div>
        <div style={{ display:"flex", gap:12, marginTop:3, flexWrap:"wrap" }}>
          {s.gender && <span style={{ fontSize:11, color:T.gray, fontFamily:T.font, display:"flex", alignItems:"center", gap:3 }}><FaVenusMars size={9}/>{s.gender}</span>}
          {s.date_of_birth && <span style={{ fontSize:11, color:T.gray, fontFamily:T.font, display:"flex", alignItems:"center", gap:3 }}><FaCalendarAlt size={9}/>Age {age(s.date_of_birth)}</span>}
          {s.join_date && <span style={{ fontSize:11, color:T.gray, fontFamily:T.font }}>Joined {fmtDate(s.join_date)}</span>}
        </div>
      </div>

      {/* Contact */}
      <div style={{ display:"flex", flexDirection:"column", gap:4, minWidth:200 }}>
        {s.parent_name && <div style={{ fontSize:12, fontWeight:700, color:T.textSub, fontFamily:T.font }}>{s.parent_name}</div>}
        {s.parent_email && (
          <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:T.gray, fontFamily:T.font }}>
            <FaEnvelope size={9}/> {s.parent_email}
          </div>
        )}
        {s.parent_phone && (
          <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:T.gray, fontFamily:T.font }}>
            <FaPhoneAlt size={9}/> {s.parent_phone}
          </div>
        )}
      </div>

      {/* Skill Level badge */}
      {s.level_name && (
        <div style={{ flexShrink:0, padding:"4px 10px", borderRadius:20, background:(s.color_hex||"#3b82f6")+"22", border:`1.5px solid ${s.color_hex||"#3b82f6"}44`, display:"flex", alignItems:"center", gap:5 }}>
          <span style={{ fontSize:10, fontWeight:800, color:s.color_hex||"#3b82f6", fontFamily:T.font }}>{s.level_name}</span>
        </div>
      )}

      {/* Status badge */}
      <div style={{ flexShrink:0, padding:"4px 10px", borderRadius:20, background:active?"rgba(16,185,129,0.10)":"rgba(239,68,68,0.10)", border:`1.5px solid ${active?"rgba(16,185,129,0.28)":"rgba(239,68,68,0.28)"}`, display:"flex", alignItems:"center", gap:5 }}>
        {active ? <FaCheckCircle size={10} style={{ color:"#10b981" }}/> : <FaBan size={10} style={{ color:"#ef4444" }}/>}
        <span style={{ fontSize:11, fontWeight:700, color:active?"#10b981":"#ef4444", fontFamily:T.font }}>{active?"Active":"Inactive"}</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// StudentRosterReport
// ══════════════════════════════════════════════════════════════════════════════
export default function StudentRosterReport() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const { config }   = useAppConfig();
  const primary      = config.primary_color   || T.blue;
  const secondary    = config.secondary_color || T.yellow;
  const fontFam      = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;

  const [students, setStudents] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [query,    setQuery]    = useState("");
  const [filter,   setFilter]   = useState("all");   // all | active | inactive
  const [gender,   setGender]   = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [sort,     setSort]     = useState("name");  // name | joined

  // ── Fetch all students ─────────────────────────────────────────────────────
  const fetchStudents = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Try /students/?limit=1000 first, fall back to search with empty string
      let res = await fetch(`${BASE}/students/?limit=1000`);
      if (!res.ok) {
        // Fallback: search with space to get all
        res = await fetch(`${BASE}/students/search?name=`);
      }
      const d = await res.json();
      const list = Array.isArray(d) ? d : (d.students || d.data || []);
      setStudents(list);
    } catch (e) {
      setError("Could not load students. Please check the backend connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const displayed = students
    .filter(s => {
      const name = `${s.first_name||""} ${s.last_name||""}`.toLowerCase();
      if (query && !name.includes(query.toLowerCase())) return false;
      const active = s.active_status === 1 || s.active_status === true;
      if (filter === "active"   && !active)  return false;
      if (filter === "inactive" &&  active)  return false;
      if (gender !== "all" && (s.gender||"").toLowerCase() !== gender) return false;
      if (levelFilter !== "all" && String(s.skill_level_id||"none") !== levelFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sort === "joined") return new Date(a.join_date||0) - new Date(b.join_date||0);
      return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
    });

  const totalActive   = students.filter(s => s.active_status === 1 || s.active_status === true).length;
  const totalInactive = students.length - totalActive;
  const totalMale     = students.filter(s => (s.gender||"").toLowerCase() === "male").length;
  const totalFemale   = students.filter(s => (s.gender||"").toLowerCase() === "female").length;

  // Skill level counts — active students only
  const activeStudents = students.filter(s => s.active_status === 1 || s.active_status === true);
  const levelCount = (name) => activeStudents.filter(s => (s.level_name||"").toLowerCase() === name.toLowerCase()).length;
  const getLevelId = (name) => {
    const match = students.find(s => (s.level_name||"").toLowerCase() === name.toLowerCase());
    return match ? String(match.skill_level_id) : name.toLowerCase();
  };
  const SKILL_LEVELS = [
    { name:"Beginner",     color:"#22c55e", bg:"rgba(34,197,94,",  id: getLevelId("Beginner")     },
    { name:"Intermediate", color:"#3b82f6", bg:"rgba(59,130,246,", id: getLevelId("Intermediate")  },
    { name:"Advanced",     color:"#f59e0b", bg:"rgba(245,158,11,", id: getLevelId("Advanced")      },
    { name:"Elite",        color:"#a855f7", bg:"rgba(168,85,247,", id: getLevelId("Elite")         },
  ];

  return (
    <div style={{ padding:"22px 30px 60px", fontFamily:fontFam, background:T.pageBg, minHeight:"100dvh" }}>

      {/* ── Header + Skill Level Cards in same row ── */}
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16, flexWrap:"wrap" }}>
        {/* Icon + title */}
        <PageHeaderIcon icon={<FaUsers size={20} color={getBrandColors().blueDark} />} />
        <div style={{ flexShrink:0 }}>
          <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:T.textPrimary, fontFamily:fontFam, letterSpacing:"-0.01em" }}>Student Roster</h2>
          <p style={{ margin:"2px 0 0", fontSize:12, color:T.gray, fontFamily:fontFam }}>
            Complete list of all registered students with contact details
          </p>
        </div>

        {/* Divider */}
        <div style={{ width:1, height:36, background:T.inputBdr, flexShrink:0, marginLeft:4 }}/>

        {/* Skill level cards — inline */}
        {SKILL_LEVELS.map(lvl => {
          const count = levelCount(lvl.name);
          const isActive = levelFilter === lvl.id;
          return (
            <div key={lvl.name} onClick={()=>{ setLevelFilter(isActive ? "all" : lvl.id); setFilter("active"); setGender("all"); }} style={{ cursor:"pointer" }}>
              <StatPill
                icon={FaMedal}
                label={lvl.name}
                value={count}
                color={lvl.color}
                bg={isActive ? `${lvl.bg}0.22)` : `${lvl.bg}0.08)`}
                bdr={`${lvl.bg}0.30)`}
              />
            </div>
          );
        })}
      </div>

      {/* ── Stats (clickable tiles — click to filter) ── */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:16 }}>
        <div onClick={()=>{setFilter("all");setGender("all");setLevelFilter("all");}} style={{ cursor:"pointer" }}>
          <StatPill icon={FaUsers}      label="Total (show all)" value={students.length} color={T.textPrimary} bg={filter==="all"&&gender==="all"&&levelFilter==="all"?T.glass:T.rowOdd} bdr={T.inputBdr}/>
        </div>
        <div onClick={()=>{setFilter("active");setGender("all");}} style={{ cursor:"pointer" }}>
          <StatPill icon={FaCheckCircle} label="Active"  value={totalActive}   color="#10b981"    bg={filter==="active"&&levelFilter==="all"?"rgba(16,185,129,0.22)":"rgba(16,185,129,0.08)"}    bdr="rgba(16,185,129,0.22)"/>
        </div>
        <div onClick={()=>{setFilter("inactive");setGender("all");}} style={{ cursor:"pointer" }}>
          <StatPill icon={FaBan}        label="Inactive" value={totalInactive} color="#ef4444"    bg={filter==="inactive"?"rgba(239,68,68,0.22)":"rgba(239,68,68,0.08)"}     bdr="rgba(239,68,68,0.22)"/>
        </div>
        <div onClick={()=>{setFilter("all");setGender("male");}} style={{ cursor:"pointer" }}>
          <StatPill icon={FaVenusMars}  label="Male"     value={totalMale}     color="#3b82f6"    bg={gender==="male"?"rgba(59,130,246,0.22)":"rgba(59,130,246,0.08)"}    bdr="rgba(59,130,246,0.22)"/>
        </div>
        <div onClick={()=>{setFilter("all");setGender("female");}} style={{ cursor:"pointer" }}>
          <StatPill icon={FaVenusMars}  label="Female"   value={totalFemale}   color="#ec4899"    bg={gender==="female"?"rgba(236,72,153,0.22)":"rgba(236,72,153,0.08)"}    bdr="rgba(236,72,153,0.22)"/>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ ...gc, padding:"14px 18px", marginBottom:20, display:"flex", gap:14, flexWrap:"wrap", alignItems:"center" }}>
        {/* Search */}
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 14px", borderRadius:10, background:T.inputBg, border:`1.5px solid ${T.inputBdr}`, flex:1, minWidth:200 }}>
          <FaSearch size={12} style={{ color:primary, flexShrink:0 }}/>
          <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search by name…"
            style={{ border:"none", outline:"none", flex:1, fontSize:13, color:T.inputText, background:"transparent", fontFamily:fontFam }}/>
          {query && <FaTimes size={11} style={{ color:T.gray, cursor:"pointer", WebkitTapHighlightColor:"transparent" }} onClick={()=>setQuery("")}/>}
        </div>

        {/* Status filter */}
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 12px", borderRadius:10, background:T.inputBg, border:`1.5px solid ${T.inputBdr}` }}>
          <FaFilter size={10} style={{ color:primary }}/>
          <select value={filter} onChange={e=>setFilter(e.target.value)}
            style={{ border:"none", outline:"none", background:"transparent", fontSize:13, color:T.inputText, fontFamily:fontFam, cursor:"pointer", WebkitTapHighlightColor:"transparent" , colorScheme: isDark ? "dark" : "light" }}>
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        {/* Skill Level filter */}
          <select value={levelFilter} onChange={e=>setLevelFilter(e.target.value)}
            style={{ padding:"8px 12px", borderRadius:8, border:`1.5px solid ${T.inputBdr}`, background:T.inputBg, color:T.inputText, colorScheme: isDark ? "dark" : "light", fontSize:13, fontFamily:T.font, cursor:"pointer", WebkitTapHighlightColor:"transparent" }}>
            <option value="all">All Levels</option>
            {[...new Map(students.filter(s=>s.skill_level_id).map(s=>[s.skill_level_id,{id:s.skill_level_id,name:s.level_name}])).values()].map(l=>(
              <option key={l.id} value={String(l.id)}>{l.name}</option>
            ))}
            {students.some(s=>!s.skill_level_id) && <option value="none">No Level Set</option>}
          </select>

        {/* Gender filter */}
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 12px", borderRadius:10, background:T.inputBg, border:`1.5px solid ${T.inputBdr}` }}>
          <FaVenusMars size={10} style={{ color:primary }}/>
          <select value={gender} onChange={e=>setGender(e.target.value)}
            style={{ border:"none", outline:"none", background:"transparent", fontSize:13, color:T.inputText, fontFamily:fontFam, cursor:"pointer", WebkitTapHighlightColor:"transparent" , colorScheme: isDark ? "dark" : "light" }}>
            <option value="all">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        {/* Sort */}
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 12px", borderRadius:10, background:T.inputBg, border:`1.5px solid ${T.inputBdr}` }}>
          <FaSortAlphaDown size={10} style={{ color:primary }}/>
          <select value={sort} onChange={e=>setSort(e.target.value)}
            style={{ border:"none", outline:"none", background:"transparent", fontSize:13, color:T.inputText, fontFamily:fontFam, cursor:"pointer", WebkitTapHighlightColor:"transparent" , colorScheme: isDark ? "dark" : "light" }}>
            <option value="name">Sort by Name</option>
            <option value="joined">Sort by Join Date</option>
          </select>
        </div>

        <span style={{ fontSize:12, color:T.gray, fontFamily:fontFam, marginLeft:"auto" }}>
          Showing <strong style={{ color:getBrandColors().yellow }}>{displayed.length}</strong> of {students.length}
        </span>
      </div>

      {/* ── List ── */}
      {loading && (
        <div style={{ textAlign:"center", padding:"60px 24px", color:T.gray, fontSize:13, fontFamily:fontFam }}>
          Loading roster…
        </div>
      )}

      {!loading && error && (
        <div style={{ padding:"16px 20px", borderRadius:12, background:"rgba(248,113,113,0.12)", backdropFilter:T.blur, WebkitBackdropFilter:T.blur, border:"1px solid rgba(248,113,113,0.35)", color:getBrandColors().red, fontSize:13, fontFamily:fontFam }}>
          {error}
        </div>
      )}


      {!loading && !error && displayed.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 24px", color:T.gray }}>
          <FaUsers size={38} style={{ opacity:0.10, display:"block", margin:"0 auto 14px" }}/>
          <p style={{ fontSize:14, fontFamily:fontFam, margin:0 }}>No students match your filters.</p>
        </div>
      )}

      {!loading && !error && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {displayed.map(s => (
            <StudentCard key={s.student_id} s={s} primary={primary} secondary={secondary}/>
          ))}
        </div>
      )}
    </div>
  );
}
